import { useEffect, useMemo, useRef } from 'react'
import { cn } from '@/lib/utils'

interface ScrollTextProps {
    text: string
    className?: string
}

type Word = { value: string, serif: boolean }

const toWords = (text: string): Array<Word> => text
    .split('*')
    .flatMap((segment, index) => segment.split(/\s+/).filter(Boolean).map(value => ({ value, serif: index % 2 === 1 })))

/**
 * Large editorial statement whose words light up one by one as it scrolls
 * through the screen. Wrap words in *asterisks* for the italic serif.
 */
export const ScrollText = ({ text, className }: ScrollTextProps) => {
    const ref = useRef<HTMLParagraphElement>(null)
    const words = useMemo(() => toWords(text), [text])
    const plain = useMemo(() => words.map(word => word.value).join(' '), [words])

    useEffect(() => {
        const node = ref.current

        if (!node) return

        const spans = Array.from(node.querySelectorAll<HTMLSpanElement>('[data-word]'))

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            spans.forEach(span => { span.style.opacity = '1' })
            return
        }

        let frame = 0

        const update = () => {
            frame = 0

            const rect = node.getBoundingClientRect()
            const viewport = window.innerHeight
            const start = viewport * 0.85
            const end = viewport * 0.3
            const distance = rect.height + (start - end)
            const progress = Math.min(1, Math.max(0, (start - rect.top) / distance))
            const lit = progress * spans.length * 1.1

            spans.forEach((span, index) => {
                const amount = Math.min(1, Math.max(0, lit - index))

                span.style.opacity = (0.14 + amount * 0.86).toFixed(3)
            })
        }

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
            cancelAnimationFrame(frame)
        }
    }, [words])

    return (
        <p ref={ref} className={className}>
            <span className='sr-only'>
                {plain}
            </span>

            <span aria-hidden='true'>
                {words.map((word, index) => (
                    <span key={index}>
                        <span data-word='' className={cn('transition-opacity duration-300', word.serif && 'em-serif')}>
                            {word.value}
                        </span>
                        {index < words.length - 1 ? ' ' : null}
                    </span>
                ))}
            </span>
        </p>
    )
}
